import { AxiosError } from 'axios'

interface IMessageHelperOptions {
  fallbackError?: string
  ignoredStatusCodes?: number[]
  overrideStatusCodes?: { code: number; message: string }[]
  withMessagePrefix?: boolean
  customMessagePrefix?: string
}

interface IMessageOverride {
  Message?: string | string[]
}

// Takes in any to convert within, instead of converting to axiosError externally.
export const axiosErrorMessageHelper = (
  err: unknown,
  options?: IMessageHelperOptions
): string | null => {
  const fallback = options?.fallbackError || ''
  if (!err) return fallback

  const axiosError = err as AxiosError

  const hasIgnoredStatusCode = options?.ignoredStatusCodes?.some(
    (code) => code === axiosError.status || axiosError.response?.status
  )

  const errorStatus = axiosError.response?.status || axiosError.status

  if (hasIgnoredStatusCode) {
    console.log('🤖 Ignored error with status code: ', errorStatus, axiosError)
    return null
  }

  const overrideMessage = options?.overrideStatusCodes?.find(
    ({ code }) => code === errorStatus
  )?.message

  if (overrideMessage) return overrideMessage

  let converter: IMessageOverride = axiosError?.response
    ?.data as IMessageOverride

  if (Object.prototype.hasOwnProperty.call(axiosError, 'Message')) {
    converter = axiosError as IMessageOverride
  }

  if (Object.prototype.hasOwnProperty.call(axiosError, 'data')) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    converter = (axiosError as any)['data'] as IMessageOverride
  }

  let message = converter?.Message

  const messagePrefix =
    options?.customMessagePrefix ||
    (options?.withMessagePrefix && 'Det oppstod en feil: ')

  // Prepend message if set in option and we got an errormessage from API
  if (messagePrefix && message)
    message = messagePrefix + (Array.isArray(message) ? message?.[0] : message)

  // Serialize axios error for better Sentry logging
  const errorDetails = {
    status: axiosError.response?.status,
    statusText: axiosError.response?.statusText,
    data: axiosError.response?.data,
    url: axiosError.config?.url,
    method: axiosError.config?.method,
    message: axiosError.message,
    request: axiosError.request ? 'Request object present' : undefined,
    // @ts-ignore
    dataMessage: axiosError?.data?.Message,
  }

  console.error(
    `👻 An ${errorStatus} error occured via axios:`,
    JSON.stringify(errorDetails, null, 2)
  )

  // Only return the first in the array, until we figure if this is a scenario that actually exists :)
  if (Array.isArray(message)) return message?.[0] || fallback
  return message || fallback
}
